import type { MouseEvent, ReactNode } from 'react';
import type { CallerCheck, Destination, Extension, FlowMessage, HoursCheck, InboundRoute, IvrMenu, Lookup, Queue, RingGroup } from '@arcnet/contracts';
import { useExtensions } from '../../lib/api/hooks.js';
import {
  useCallerChecks, useHoursChecks, useInboundRoutes, useIvrs, useLookups, useMailboxes, useMessages, useQueues, useRingGroups,
} from '../../lib/api/hooks-tms.js';
import { useAccountRoute } from '../../lib/subroute.js';
import { nameSlugs } from '../../lib/openitem.js';
import { formatDuration } from '../../lib/format.js';

/**
 * Where a call goes, followed to the end and linked at every step (UX review, 21 October 2026;
 * owner: "build both" -- Assigned numbers and the route editor's "What happens").
 *
 * A route's card used to stop at its first step's name, and Assigned numbers at the route's name.
 * Here each step names what it is, links to its own page (/ivrs/main-menu, /queues/support), and
 * says what happens next: an IVR's keys, a hunt list's "no answer", a queue's timeout. The
 * flowchart window (RouteFlow) draws the same thing as a picture; this is the version that is
 * always on the page.
 *
 * Built from the destinations themselves, so the route editor can show its unsaved draft.
 */

interface Mailbox { id: string; extension_number: string; extension_name: string }

export interface RoutingLookup {
  ext: Map<string, Extension>;
  groups: Map<string, RingGroup>;
  queues: Map<string, Queue>;
  ivrs: Map<string, IvrMenu>;
  messages: Map<string, FlowMessage>;
  hours: Map<string, HoursCheck>;
  callers: Map<string, CallerCheck>;
  lookups: Map<string, Lookup>;
  flows: Map<string, InboundRoute>;
  boxes: Map<string, Mailbox>;
  /** Each piece's address name, as its own page reads it (openitem.ts). */
  slug: Map<string, string>;
}

const byId = <T extends { id: string }>(xs: T[] | undefined): Map<string, T> => new Map((xs ?? []).map((x) => [x.id, x]));

export function useRoutingLookup(tenantId: string): RoutingLookup & { routes: InboundRoute[] } {
  const extensions = useExtensions(tenantId, { limit: 1000 });
  const groups = useRingGroups(tenantId);
  const queues = useQueues(tenantId);
  const ivrs = useIvrs(tenantId);
  const messages = useMessages(tenantId);
  const hours = useHoursChecks(tenantId);
  const callers = useCallerChecks(tenantId);
  const lookups = useLookups(tenantId);
  const boxes = useMailboxes(tenantId);
  const routes = useInboundRoutes(tenantId);
  const slug = new Map<string, string>();
  const named = <T extends { id: string; name: string }>(xs: T[] | undefined) => {
    for (const [id, s] of nameSlugs(xs ?? [], (x) => x.name)) slug.set(id, s);
  };
  named(groups.data?.data); named(queues.data?.data); named(ivrs.data?.data);
  named(messages.data?.data); named(hours.data?.data); named(routes.data?.data);
  named(callers.data?.data); named(lookups.data?.data);
  return {
    ext: byId(extensions.data?.data), groups: byId(groups.data?.data), queues: byId(queues.data?.data),
    ivrs: byId(ivrs.data?.data), messages: byId(messages.data?.data), hours: byId(hours.data?.data),
    callers: byId(callers.data?.data), lookups: byId(lookups.data?.data), flows: byId(routes.data?.data),
    boxes: byId(boxes.data?.data as Mailbox[] | undefined), slug,
    routes: routes.data?.data ?? [],
  };
}

/** 010 500 0371 for a South African number, the way people read and dial it; others as they are. */
export function nationalNumber(e164: string): string {
  const m = /^\+27(\d{2})(\d{3})(\d{4})$/.exec(e164);
  return m ? `0${m[1]} ${m[2]} ${m[3]}` : e164;
}

/** What a destination is called, and the address of its page (none for a number or a hang-up). */
export function describe(d: Destination | null, L: RoutingLookup): { kind: string; name: string; rest: string[] | null } {
  if (!d) return { kind: '', name: 'Nowhere set', rest: null };
  const at = (section: string, id: string) => {
    const s = L.slug.get(id);
    return s ? [section, s] : null;
  };
  switch (d.type) {
    case 'extension': {
      const e = L.ext.get(d.id);
      return e ? { kind: 'Extension', name: `${e.number} ${e.display_name}`, rest: ['extensions', e.number] }
        : { kind: 'Extension', name: 'deleted', rest: null };
    }
    case 'ring_group': return { kind: 'Hunt list', name: L.groups.get(d.id)?.name ?? 'deleted', rest: at('ring-groups', d.id) };
    case 'queue': return { kind: 'Queue', name: L.queues.get(d.id)?.name ?? 'deleted', rest: at('queues', d.id) };
    case 'ivr': return { kind: 'IVR menu', name: L.ivrs.get(d.id)?.name ?? 'deleted', rest: at('ivrs', d.id) };
    case 'message': {
      const s = L.slug.get(d.id);
      return { kind: 'Message', name: L.messages.get(d.id)?.name ?? 'deleted', rest: s ? ['call-steps', 'messages', s] : null };
    }
    case 'hours': {
      const s = L.slug.get(d.id);
      return { kind: 'Hours check', name: L.hours.get(d.id)?.name ?? 'deleted', rest: s ? ['call-steps', 'hours-checks', s] : null };
    }
    case 'caller': {
      const s = L.slug.get(d.id);
      return { kind: 'Caller check', name: L.callers.get(d.id)?.name ?? 'deleted', rest: s ? ['call-steps', 'caller-checks', s] : null };
    }
    case 'lookup': {
      const s = L.slug.get(d.id);
      return { kind: 'Look up', name: L.lookups.get(d.id)?.name ?? 'deleted', rest: s ? ['call-steps', 'lookups', s] : null };
    }
    case 'route': {
      const s = L.slug.get(d.id);
      const r = L.flows.get(d.id);
      return { kind: 'Call flow', name: r?.name ?? 'deleted', rest: s ? ['inbound', s] : null };
    }
    case 'voicemail': {
      const b = L.boxes.get(d.id);
      return { kind: 'Voicemail', name: b ? `${b.extension_number} ${b.extension_name}` : 'a mailbox',
        rest: b ? ['extensions', b.extension_number, 'voicemail'] : null };
    }
    case 'external': return { kind: 'Forwards to', name: nationalNumber(d.number), rest: null };
    case 'hangup': return { kind: '', name: 'Hangs up', rest: null };
    default: return { kind: '', name: 'Unknown', rest: null };
  }
}

/** A routing piece named as a link to its page: "IVR menu · Main menu". */
export function DestLink({ kind, name, rest }: { kind: string; name: string; rest: string[] | null }) {
  const account = useAccountRoute();
  const body = <>{kind && <span className="arc-dest-kind">{kind}</span>}<span className="arc-dest-name">{name}</span></>;
  if (!rest || !account) return <span className="arc-dest">{body}</span>;
  const open = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    account.go(rest);
  };
  return <a className="arc-dest" href={account.href(rest)} onClick={open}>{body}</a>;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** One step's own words: what it does before the call moves on. */
function detailOf(d: Destination, L: RoutingLookup): string {
  switch (d.type) {
    case 'extension': return 'the extension’s forwarding and voicemail apply';
    case 'ring_group': {
      const g = L.groups.get(d.id);
      if (!g) return '';
      return g.steps.length > 1 ? `${g.steps.length} steps` : `rings ${plural(g.members.length, 'member', 'members')} ${
        g.strategy === 'simultaneous' ? 'at once' : g.strategy === 'random' ? 'in random order' : 'in order'}, ${g.ring_time_sec}s${g.strategy === 'simultaneous' ? '' : ' each'}`;
    }
    case 'queue': {
      const q = L.queues.get(d.id);
      return q ? `${plural(q.agents.length, 'agent', 'agents')}, callers wait up to ${formatDuration(q.max_wait_sec)}` : '';
    }
    case 'ivr': {
      const m = L.ivrs.get(d.id);
      return m ? (m.greeting_media_id ? 'plays its greeting' : 'no greeting yet') : '';
    }
    case 'message': {
      const m = L.messages.get(d.id);
      return m ? (m.media_name ? `plays ${m.media_name}` : 'no recording yet') : '';
    }
    case 'hours': {
      const h = L.hours.get(d.id);
      if (!h) return '';
      const on = h.hours.mode === 'own' ? 'its own hours' : h.hours.schedule_name ?? 'a schedule';
      return h.hours.open_now === null ? on : `${on}, ${h.hours.open_now ? 'open now' : 'closed now'}`;
    }
    case 'caller': {
      const c = L.callers.get(d.id);
      return c ? `${plural(c.numbers.length, 'number', 'numbers')}${c.match_withheld ? ' and hidden numbers' : ''}` : '';
    }
    case 'lookup': {
      const l = L.lookups.get(d.id);
      if (!l) return '';
      let host = l.url;
      try { host = new URL(l.url).hostname; } catch { /* as typed */ }
      return `asks ${host}, reads ${l.field}`;
    }
    case 'route': return 'its own hours and steps from here';
    case 'voicemail': return 'the caller leaves a message';
    case 'external': return 'over the account’s outbound routing';
    default: return '';
  }
}

/** Where the call can go after this step, each with when. */
function branchesOf(d: Destination, L: RoutingLookup): Array<{ when: string; to: Destination | null }> {
  switch (d.type) {
    case 'ring_group': {
      const g = L.groups.get(d.id);
      return g ? [{ when: 'No answer', to: g.timeout_destination }] : [];
    }
    case 'queue': {
      const q = L.queues.get(d.id);
      if (!q) return [];
      return [
        { when: 'Not answered in time', to: q.timeout_destination },
        ...(q.exit_key && q.exit_destination ? [{ when: `Caller presses ${q.exit_key}`, to: q.exit_destination }] : []),
      ];
    }
    case 'ivr': {
      const m = L.ivrs.get(d.id);
      if (!m) return [];
      const keys = [...m.options].sort((a, b) => a.digit.localeCompare(b.digit));
      return [
        ...keys.map((o) => ({ when: `Key ${o.digit}${o.label ? ` · ${o.label}` : ''}`, to: o.destination })),
        { when: 'No key', to: m.timeout_destination },
        ...(m.invalid_destination ? [{ when: 'Wrong key', to: m.invalid_destination }] : []),
      ];
    }
    case 'message': {
      const m = L.messages.get(d.id);
      return m ? [{ when: 'Then', to: m.next_destination }] : [];
    }
    case 'hours': {
      const h = L.hours.get(d.id);
      return h ? [{ when: 'Open', to: h.open_destination }, { when: 'Closed', to: h.closed_destination }] : [];
    }
    case 'caller': {
      const c = L.callers.get(d.id);
      return c ? [{ when: 'On the list', to: c.match_destination }, { when: 'Anyone else', to: c.no_match_destination }] : [];
    }
    case 'lookup': {
      const l = L.lookups.get(d.id);
      return l ? [
        ...l.cases.map((c) => ({ when: `Answers “${c.value}”`, to: c.destination })),
        { when: 'Any other answer', to: l.no_match_destination },
        { when: 'No answer', to: l.error_destination },
      ] : [];
    }
    default: return [];
  }
}

/** Menus that lead back into menus stop being useful to show long before they stop. */
const MAX_DEPTH = 5;

function Step({ when, to, L, depth, seen, none }: {
  when?: string | undefined; to: Destination | null; L: RoutingLookup; depth: number; seen: ReadonlySet<string>;
  /** What an empty destination means here: a route refuses the call, a menu's timeout hangs up. */
  none: 'refused' | 'hangup';
}) {
  const id = to && 'id' in to ? to.id : null;
  const again = id !== null && seen.has(id);
  const branches = to && !again && depth < MAX_DEPTH ? branchesOf(to, L) : [];
  const detail = to && !again ? detailOf(to, L) : '';
  const next = id ? new Set([...seen, id]) : seen;
  return (
    <li>
      <div className="arc-path-line">
        {when && <span className="arc-path-when">{when}</span>}
        {to === null
          ? <span className="arc-subtle">{none === 'refused' ? 'Nowhere set: the call is refused' : 'Hangs up'}</span>
          : <>
            {again && <span className="arc-subtle">back to</span>}
            <DestLink {...describe(to, L)} />
            {detail && <span className="arc-subtle">{detail}</span>}
          </>}
      </div>
      {branches.length > 0 && (
        <ul className="arc-path">
          {branches.map((b) => <Step key={b.when} when={b.when} to={b.to} L={L} depth={depth + 1} seen={next} none="hangup" />)}
        </ul>
      )}
    </li>
  );
}

/** A route's whole path: the hours split, then every step to the end, linked. */
export function CallPath({ destination, closed, hoursLabel, openNow, enabled, L }: {
  destination: Destination | null;
  closed: Destination | null;
  /** Null when the route always sends calls the same way. */
  hoursLabel: string | null;
  openNow?: boolean | null | undefined;
  enabled: boolean;
  L: RoutingLookup;
}): ReactNode {
  if (!enabled) return <p className="arc-muted" style={{ margin: 0 }}>Switched off: calls to these numbers are refused.</p>;
  if (hoursLabel === null) {
    return <ul className="arc-path arc-path-root"><Step to={destination} L={L} depth={0} seen={new Set()} none="refused" /></ul>;
  }
  return (
    <>
      <p className="arc-subtle" style={{ margin: '0 0 6px', fontSize: 12.5 }}>
        {hoursLabel}{openNow === true ? ', open now' : openNow === false ? ', closed now' : ''}
      </p>
      <ul className="arc-path arc-path-root">
        <Step when="Open" to={destination} L={L} depth={0} seen={new Set()} none="refused" />
        <Step when="Closed" to={closed} L={L} depth={0} seen={new Set()} none="refused" />
      </ul>
    </>
  );
}

/**
 * The same path in one table cell (Assigned numbers): each branch on a line, its first step
 * linked, and in a few words what comes after it.
 */
export function CallPathShort({ route, L }: { route: InboundRoute; L: RoutingLookup }) {
  if (!route.is_enabled) return <span className="arc-muted" style={{ fontSize: 12.5 }}>Route switched off: calls are refused</span>;
  const after = (d: Destination | null): string => {
    if (!d) return '';
    if (d.type === 'ivr') {
      const m = L.ivrs.get(d.id);
      return m && m.options.length > 0
        ? `keys ${[...m.options].sort((a, b) => a.digit.localeCompare(b.digit)).map((o) => `${o.digit}${o.label ? ` ${o.label}` : ''}`).join(' · ')}`
        : 'no keys set';
    }
    const b = branchesOf(d, L)[0];
    if (b && (d.type === 'ring_group' || d.type === 'queue' || d.type === 'message')) {
      const x = describe(b.to, L);
      return `${b.when.toLowerCase()}: ${x.kind ? `${x.kind.toLowerCase()} ` : ''}${x.name}`;
    }
    return '';
  };
  const line = (when: string | null, d: Destination | null) => (
    <div className="arc-goes-line" key={when ?? 'always'}>
      {when && <span className="arc-goes-when">{when}</span>}
      {d ? <DestLink {...describe(d, L)} /> : <span className="arc-subtle">Calls are refused</span>}
      {after(d) && <span className="arc-subtle" style={{ fontSize: 11.5 }}>{after(d)}</span>}
    </div>
  );
  return (
    <div className="arc-goes">
      {route.hours.mode === 'always'
        ? line(null, route.destination)
        : [line('Open', route.destination), line('Closed', route.closed_destination)]}
    </div>
  );
}
